import { BaggageReconciliationDetailsModel, RegisterBookingPaymentModel, SellOnTheGoReconciliationDetailsModel } from '@/__generated';
import ZTable, { IColumnType, ITableRef } from '@/components/ZTable';
import { ROW_GUTTER, formatDisplayFullDate, ModalWidth } from '@/config';
import { formatCurrency } from '@/helpers/format';
import MainContent from '@/layouts/MainContent';
import { Row, Col, Select, Tabs, Card, Form, Checkbox, InputNumber, Button, Space, Result, Modal, Spin, Tag } from 'antd';
import form from 'antd/lib/form';
import TextArea from 'antd/lib/input/TextArea';
import TabPane from 'antd/lib/tabs/TabPane';
import React from 'react';
import { useTranslation } from 'react-i18next';
import EmployeeCategoryInput from '@/components/FormItems/EmployeeCategoryInput';
import { TransportRoleType } from '@/constants';
import { apiSdk } from '@/httpClient';
import { getFullName } from '@/helpers/string';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';

const columns: IColumnType<RegisterBookingPaymentModel>[] = [
  {
    title: 'baggageReconciliation__TripName',
    dataIndex: 'tripName',
    ellipsis: true,
  },
  {
    title: 'ticketBooking_BookingRefNo',
    dataIndex: 'bookingRefNo',
    ellipsis: true,
  },
  {
    title: 'ticketBooking_TicketRefNo',
    dataIndex: 'ticketRefNo',
    ellipsis: true,
  },
  {
    title: 'registerReconciliation_PaymentDate',
    dataIndex: 'paymentDate',
    ellipsis: true,
    render: v => formatDisplayFullDate(v),
    width: 170,
    sorter: true,
  },
  {
    title: 'baggageReconciliation__BookingDate',
    dataIndex: 'bookingDate',
    ellipsis: true,
    render: v => formatDisplayFullDate(v),
    width: 170,
    sorter: true,
  },
  {
    title: 'base.Amount',
    dataIndex: 'amount',
    ellipsis: true,
    width: 170,
    render: v => <b>{formatCurrency(v)}</b>,
    sorter: true,
  },
];

const PAGE_TITLE = 'baggageReconciliation_Title';
export default function BaggageReconciliationPage() {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [loading, setLoading] = React.useState<boolean>(false);
  const zTb = React.useRef<ITableRef>();
  const [selectedUserId, setSelectedUserId] = React.useState<string>();
  const [drivers, setDrivers] = React.useState<Array<{ label: string; value: string }>>([]);
  const [reconciliationDetail, setReconciliationDetail] = React.useState<BaggageReconciliationDetailsModel>();
  const [displayModal, setDisplayModal] = React.useState<boolean>(false);
  const [isCrossVerified, setIsCrossVerified] = React.useState(true);

  React.useEffect(() => {
    if (selectedUserId) {
      setLoading(true);
      apiSdk.BaggageReconciliationService.getEmployeeReconciliationDetails({ employeeId: selectedUserId })
        .then(res => setReconciliationDetail(res))
        .finally(() => {
          setLoading(false);
        });
    } else {
      setReconciliationDetail(undefined);
    }
  }, [selectedUserId]);
  React.useEffect(() => {
    apiSdk.CompanyUserService.getUserByRoles({ roles: [TransportRoleType.CheckInOperator] }).then(res => {
      setDrivers(res.map(d => ({ value: d.id || '', label: `${getFullName(d.firstName, d.lastName)}` })));
    });
  }, []);

  React.useEffect(() => {
    if (!displayModal) {
      setIsCrossVerified(true);
      form.resetFields();
    }
  }, [displayModal]);
  const logReconciliation = () => {
    form.validateFields().then(res => {
      setLoading(true);
      apiSdk.BaggageReconciliationService.logReconciliationRecord({
        body: {
          amountDifference: isCrossVerified ? undefined : res.amountDifference,
          employeeId: selectedUserId || '',
          comments: res.comments,
          isCrossVerified: isCrossVerified,
          totalReconciliationAmount: reconciliationDetail?.totalHeldAmount,
        },
      })
        .then(res => {
          if (res.success) {
            zTb.current?.reload();
            setDisplayModal(false);
            setReconciliationDetail(undefined);
          } else {
            defaultApiErrorHandle(res);
          }
        })
        .catch(defaultApiErrorHandle)
        .finally(() => {
          setLoading(false);
        });
    });
  };
  return (
    <MainContent title={t(PAGE_TITLE)}>
      <div className="ant-layout-content-body">
        <Row gutter={[ROW_GUTTER, ROW_GUTTER]}>
          <Col md={24}>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Space>
                <div>{t('sellOnTheGoReconciliation_SelectDriverOrCoDriver')}</div>
                <Select style={{ width: 250 }} onChange={d => setSelectedUserId(d)} options={drivers} showSearch={true}></Select>
              </Space>
              {reconciliationDetail ? (
                <div>
                  <Space>
                    <div>
                      {t('sellOnTheGoReconciliation_TotalBookingAmount')}:
                      <b style={{ marginLeft: 10 }}>{formatCurrency(reconciliationDetail?.totalAmount)}</b>
                    </div>
                    <div>
                      {t('baggageReconciliation_WithoutTicket')}:
                      <b style={{ marginLeft: 10 }}>{formatCurrency(reconciliationDetail?.baggageWithoutTicketAmount)}</b>
                      {reconciliationDetail?.baggageWithoutTicketAmount ? <Tag color="red" style={{ marginLeft: 8 }}>{t('baggageReconciliation_ToReview')}</Tag> : null}
                    </div>
                    <div>
                      {t('baggageReconciliation_TotalHeld')}:
                      <b style={{ marginLeft: 10 }}>{formatCurrency(reconciliationDetail?.totalHeldAmount)}</b>
                    </div>
                    <div>
                      {t('registerReconciliation_LastReconciliation')}:
                      <b style={{ marginLeft: 10 }}>{formatDisplayFullDate(reconciliationDetail?.lastReconciliationDate) || 'N/A'}</b>
                    </div>
                    <Button
                      type="primary"
                      disabled={!reconciliationDetail?.allowReconciliation}
                      onClick={() => {
                        setDisplayModal(true);
                      }}
                    >
                      {t('Log Reconciliation')}
                    </Button>
                  </Space>
                </div>
              ) : null}
            </div>
          </Col>
          <Col md={24}>
            {selectedUserId ? (
              <ZTable<RegisterBookingPaymentModel>
                columns={columns}
                key={selectedUserId}
                primaryKey="id"
                url="/bookings/baggage-reconciliations/get-employee-reconciliation-payment-records"
                bordered
                defaultFilter={{ employeeId: selectedUserId }}
                tableRef={zTb}
                order_by="createdOn"
                order_by_asc={true}
                allowEdit={false}
                allowDelete={false}
                searchBtnAlignLeft
              />
            ) : (
              <div>
                <Result status="warning" subTitle="Please select check-in user" />
              </div>
            )}
          </Col>
        </Row>
      </div>
      {displayModal ? (
        <Modal
          open={displayModal}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={'Log Reconciliation'}
          forceRender={true}
          okText={t('registerReconciliation_LogReconciliation')}
          okButtonProps={{ className: 'save-btn' }}
          onOk={logReconciliation}
          onCancel={() => setDisplayModal(false)}
        >
          <Spin spinning={loading}>
            <Form form={form} size="middle" layout="vertical">
              <Form.Item>
                <Checkbox
                  checked={isCrossVerified}
                  onChange={e => {
                    setIsCrossVerified(x => !x);
                  }}
                >
                  {t('registerReconciliation_IsAmountVerified')}
                </Checkbox>
              </Form.Item>
              <Form.Item name="comments">
                <TextArea placeholder={t('base.Comment')} />
              </Form.Item>
              <Form.Item label={t('')} name="amountDifference">
                <InputNumber addonAfter="FCFA" disabled={isCrossVerified} />
              </Form.Item>
            </Form>
          </Spin>
        </Modal>
      ) : null}
    </MainContent>
  );
}
